import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, RefreshCw, Check, AlertCircle, Loader2, Sparkles, ChevronDown, Lock,
  ChefHat, Timer, Flame, Droplets, Wheat, Egg, Fish, Drumstick, Carrot,
  Salad, Coffee, Cookie, Apple, Beef, Milk, Utensils, Bean, Nut, Banana,
  Cherry, Citrus, Sprout, Candy
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { NutritionPlan, MealOption } from '../types';
import NutritionLoadingScreen from '../components/NutritionLoadingScreen'; // IMPORTED
import { useAuth } from '../contexts/AuthContext';
import { NutritionDashboardSkeleton } from '../components/skeletons/NutritionDashboardSkeleton';
import { useDietStatus } from '../hooks/useDietStatus';
import { getIngredientBenefit } from '../data/ingredientInfo';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PendingPhotosModal } from '../components/PendingPhotosModal';

// --- VISUAL ASSETS MAPPING 🎨 ---
const getIngredientIcon = (name: string, className = "w-4 h-4 text-white/40") => {
  if (!name) return '•';
  const n = name.toLowerCase();

  // Proteins (Land & Sea)
  if (n.includes('huevo') || n.includes('clara') || n.includes('yema') || n.includes('tortilla')) return <Egg className={className} />;
  if (n.includes('pollo') || n.includes('pavo') || n.includes('ave') || n.includes('pechuga')) return <Drumstick className={className} />;
  if (n.includes('ternera') || n.includes('buey') || n.includes('carne') || n.includes('lomo') || n.includes('jamón') || n.includes('serrano')) return <Beef className={className} />;
  if (n.includes('pescado') || n.includes('salmón') || n.includes('atún') || n.includes('merluza') || n.includes('bacalao') || n.includes('lubina') || n.includes('dorada')) return <Fish className={className} />;
  if (n.includes('gamba') || n.includes('langostino') || n.includes('calamar') || n.includes('sepia') || n.includes('mejillón') || n.includes('marisco')) return <Fish className={className} />;

  // Plant Protein & Dairy
  if (n.includes('whey') || n.includes('proteína') || n.includes('batido')) return <Milk className={className} />;
  if (n.includes('yogur') || n.includes('skyr') || n.includes('kefir') || n.includes('queso') || n.includes('leche') || n.includes('requesón') || n.includes('burrata') || n.includes('mozzarella')) return <Milk className={className} />;
  if (n.includes('lentejas') || n.includes('garbanzos') || n.includes('alubias') || n.includes('frijoles') || n.includes('soja') || n.includes('tofu') || n.includes('tempeh') || n.includes('seitan')) return <Bean className={className} />;

  // Carbs
  if (n.includes('arroz') || n.includes('risotto')) return <Wheat className={className} />;
  if (n.includes('avena') || n.includes('porridge') || n.includes('granola') || n.includes('muesli')) return <Wheat className={className} />;
  if (n.includes('pasta') || n.includes('espaguetis') || n.includes('macarrones') || n.includes('fideos') || n.includes('tallarines')) return <Wheat className={className} />;
  if (n.includes('pan') || n.includes('tostada') || n.includes('wrap') || n.includes('tortita') || n.includes('bagel')) return <Wheat className={className} />;
  if (n.includes('quinoa') || n.includes('cuscus') || n.includes('trigo')) return <Wheat className={className} />;
  if (n.includes('patata') || n.includes('boniato') || n.includes('batata') || n.includes('yuca')) return <Cookie className={className} />;

  // Fats
  if (n.includes('aguacate') || n.includes('guacamole')) return <Droplets className={className} />;
  if (n.includes('aceite') || n.includes('aove') || n.includes('mantequilla') || n.includes('ghee')) return <Droplets className={className} />;
  if (n.includes('nuez') || n.includes('nueces') || n.includes('almendra') || n.includes('avellana') || n.includes('anacardo') || n.includes('pistacho') || n.includes('cacahuete') || n.includes('crema de')) return <Nut className={className} />;
  if (n.includes('chía') || n.includes('lino') || n.includes('semillas') || n.includes('pipas')) return <Nut className={className} />;

  // Fruits
  if (n.includes('manzana') || n.includes('pera')) return <Apple className={className} />;
  if (n.includes('plátano') || n.includes('banana')) return <Banana className={className} />;
  if (n.includes('fresa') || n.includes('frutos rojos') || n.includes('arándano') || n.includes('frambuesa') || n.includes('cereza') || n.includes('mora')) return <Cherry className={className} />;
  if (n.includes('naranja') || n.includes('mandarina') || n.includes('limón') || n.includes('pomelo') || n.includes('cítrico')) return <Citrus className={className} />;
  if (n.includes('piña') || n.includes('mango') || n.includes('kiwi') || n.includes('melón') || n.includes('sandía') || n.includes('fruta')) return <Apple className={className} />;

  // Veggies
  if (n.includes('ensalada') || n.includes('lechuga') || n.includes('espinaca') || n.includes('rúcula') || n.includes('canónigos') || n.includes('acelga') || n.includes('kale')) return <Salad className={className} />;
  if (n.includes('brócoli') || n.includes('coliflor') || n.includes('repollo') || n.includes('col')) return <Sprout className={className} />;
  if (n.includes('zanahoria') || n.includes('calabaza')) return <Carrot className={className} />;
  if (n.includes('tomate') || n.includes('cherry')) return <Apple className={className} />;
  if (n.includes('pepino') || n.includes('calabacín') || n.includes('berenjena') || n.includes('pimiento') || n.includes('cebolla') || n.includes('champiñón') || n.includes('setas') || n.includes('verdura')) return <Salad className={className} />;

  // Sweets & Misc
  if (n.includes('miel') || n.includes('sirope') || n.includes('azúcar')) return <Candy className={className} />;
  if (n.includes('chocolate') || n.includes('cacao')) return <Cookie className={className} />;
  if (n.includes('café') || n.includes('té')) return <Coffee className={className} />;
  if (n.includes('agua') || n.includes('infusión')) return <Droplets className={className} />;

  // Default
  return <Utensils className={className} />;
};

// V425: PLACEHOLDER PLAN FOR BASIC USERS (The Illusion)
const PLACEHOLDER_PLAN: NutritionPlan = {
  id: 'placeholder',
  userId: 'placeholder',
  generatedDate: new Date().toISOString(),
  nombre_usuario: 'Atleta Aura',
  resumen_diario: {
    total_kal: 2450,
    objetivo_actual: 'Ganancia Muscular',
    tipo_dia: 'Entrenamiento',
    macros: { proteina: '180g', carbos: '300g', grasas: '80g' }
  },
  comidas: [
    {
      orden: 1, nombre_comida: 'Desayuno Anaeróbico', etiqueta_timing: 'Pre-Workout', horario_sugerido: '08:00', explicacion_timing: '', insight_valor: '',
      opciones: [{ id: 1, nombre_plato: 'Tortitas de Avena y Claras', instrucciones_rapidas: '', ingredientes: [{ alimento: 'Claras de huevo', gramos_crudo: 200 }, { alimento: 'Avena', gramos_crudo: 80 }] }]
    },
    {
      orden: 2, nombre_comida: 'Almuerzo', etiqueta_timing: 'Carga', horario_sugerido: '14:00', explicacion_timing: '', insight_valor: '',
      opciones: [{ id: 1, nombre_plato: 'Pollo con Arroz y Verduras', instrucciones_rapidas: '', ingredientes: [{ alimento: 'Pechuga de pollo', gramos_crudo: 150 }, { alimento: 'Arroz basmati', gramos_crudo: 100 }] }]
    },
    {
      orden: 3, nombre_comida: 'Cena', etiqueta_timing: 'Recuperación', horario_sugerido: '21:00', explicacion_timing: '', insight_valor: '',
      opciones: [{ id: 1, nombre_plato: 'Salmón con Patata', instrucciones_rapidas: '', ingredientes: [{ alimento: 'Salmón', gramos_crudo: 150 }, { alimento: 'Patata', gramos_crudo: 200 }] }]
    }
  ]
};

// V528: STABLE ZOMBIE LOADING WRAPPER
// Ensures start/target times are calculated ONCE on mount, preventing
// infinite reset loops when parent re-renders.
const NutritionLoadingContainer: React.FC<{
  onSkip: () => void;
  status: string;
}> = ({ onSkip, status }) => {
  const { processStartTime, targetCompletionTime } = useDietStatus();
  
  // V1305: Sync visual loading screen entirely with the cloud/global worker timer
  const start = processStartTime || Date.now();
  const target = targetCompletionTime || (start + 300000);

  return (
    <NutritionLoadingScreen
      minutesLeft={Math.ceil((target - Date.now()) / 60000)}
      startTime={start}
      targetTime={target}
      onDevComplete={onSkip}
      isPerformingLoad={true}
    />
  );
};
const NutritionDashboardScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, subscriptionStatus, loading: authLoading, marcAvatarUrl } = useAuth();

  // V500: CLOUD-FIRST DATA SOURCE
  const { status, plan, isLoading: hookLoading, isSyncingCloud, isGenerating, processStartTime, targetCompletionTime, triggerGeneration, needsDietReveal, clearDietReveal, generationType } = useDietStatus();

  // Local UI State
  const [forceUnlockSkeleton, setForceUnlockSkeleton] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({});
  const [expandedMeals, setExpandedMeals] = useState<Record<number, boolean>>({});
  const [expandedStrategies, setExpandedStrategies] = useState<Record<number, boolean>>({});
  const [expandedSupplement, setExpandedSupplement] = useState<number | null>(null);
  const [isSupplementsSectionExpanded, setIsSupplementsSectionExpanded] = useState(false);
  const [completedMeals, setCompletedMeals] = useState<string[]>([]);

  useEffect(() => {
    const today = new Date();
    const dateKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
    const stored = localStorage.getItem('aura_diet_tracking');
    if (stored) {
      try {
        const trackingObj = JSON.parse(stored);
        const mealsForToday = trackingObj[dateKey] || [];
        setCompletedMeals(mealsForToday);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const toggleMealCompletion = (mealIdOrIndex: string) => {
    const today = new Date();
    const dateKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
    setCompletedMeals(prev => {
      const next = prev.includes(mealIdOrIndex)
        ? prev.filter(id => id !== mealIdOrIndex)
        : [...prev, mealIdOrIndex];
      
      let trackingObj: Record<string, string[]> = {};
      const stored = localStorage.getItem('aura_diet_tracking');
      if (stored) {
        try {
          trackingObj = JSON.parse(stored);
        } catch (e) {
          console.error(e);
        }
      }
      trackingObj[dateKey] = next;
      localStorage.setItem('aura_diet_tracking', JSON.stringify(trackingObj));
      
      window.dispatchEvent(new CustomEvent('storage'));
      window.dispatchEvent(new CustomEvent('aura_diet_updated'));
      
      return next;
    });
  };

  // V1003: Reveal overlay controlled strictly by Supabase
  const [hasDismissedReveal, setRevealDismissedState] = useState(false);
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  
  useEffect(() => {
    // V6042: Prevent read-replica ghosts from un-dismissing the reveal!
    if (needsDietReveal) {
        if (plan) {
            const currentPlanId = plan.generatedDate || plan.id;
            const lastRevealed = localStorage.getItem('aura_last_revealed_plan_id');
            if (lastRevealed === currentPlanId) {
                setRevealDismissedState(true);
                return;
            }
        }

        // If it's genuinely a NEW reveal, wipe stale session blockers
        if (sessionStorage.getItem('aura_diet_chat_revealed') === 'true') {
            sessionStorage.removeItem('aura_diet_chat_revealed');
            setRevealDismissedState(false);
        }
        if (localStorage.getItem('aura_pending_completion_reveal') === 'true') {
            localStorage.removeItem('aura_pending_completion_reveal');
            setRevealDismissedState(false);
        }
    }
  }, [needsDietReveal, plan]);

  const [timerFinishedLocally, setTimerFinishedLocally] = useState(() => {
      try {
          const flatStatus = localStorage.getItem('aura_diet_status');
          const flatTarget = localStorage.getItem('aura_target_completion_time');
          const cached = localStorage.getItem('aura_user_data');
          if (cached) {
             const parsed = JSON.parse(cached);
             const currentStatus = flatStatus || parsed.aura_diet_status;
             if (currentStatus === 'completed' && parsed.needs_diet_reveal) {
                 const target = flatTarget ? parseInt(flatTarget, 10) : parsed.aura_target_completion_time;
                 if (!target || Date.now() >= target) {
                     return true;
                 }
             }
          }
      } catch(e) {}
      return false;
  });

  // V6037: Deterministic "Missed Loading Screen" detection
  // We cannot use a hardcoded 3000ms timer because slow connections or cold-starts will exceed it.
  // Instead, we track if the app was EVER genuinely generating AFTER the initial cloud sync.
  const wasGenuinelyGeneratingRef = useRef(false);
  const missedLoadingScreenRef = useRef(false);

  if (!isSyncingCloud && (status === 'generating_initial' || status === 'processing' || status === 'trigger_update' || status === 'updating')) {
      wasGenuinelyGeneratingRef.current = true;
  }

    // (Moved missedLoadingScreenRef block below maxTargetRef initialization)

  // V1005: Protect target time from jumping to the past when Supabase clears it mid-countdown
  const maxTargetRef = useRef<number | null>(null);

  // V6033: Respect visual timer!
  const nowForCalc = Date.now();
  const MIN_DUR = (generationType === 'new' || status === 'generating_initial') ? 5400000 : 1800000;
  const isActuallyFinishedCheck = status === 'completed' || status === 'error_generation' || status === 'error_finalization';
  let targetCalc = targetCompletionTime;
  if (!targetCalc) {
      targetCalc = processStartTime ? (processStartTime + MIN_DUR) : null;
  }
  
  // V1005: Protect target time from jumping to the past when Supabase clears it mid-countdown
  // (We use targetCalc to update the maxTargetRef, then use the maxTargetRef for the final prediction)
  if (targetCalc) {
      if (!maxTargetRef.current || targetCalc > maxTargetRef.current) {
          maxTargetRef.current = targetCalc;
      }
  }

  // If the backend says 'completed' but we never saw it genuinely generating, they missed the loading screen.
  if (!authLoading && !isSyncingCloud && status === 'completed' && needsDietReveal && !missedLoadingScreenRef.current && !timerFinishedLocally) {
      if (!wasGenuinelyGeneratingRef.current) {
          // V2108: Only assume they missed it if the real-world time has actually passed
          if (maxTargetRef.current === null || Date.now() >= maxTargetRef.current) {
              missedLoadingScreenRef.current = true;
          }
      }
  }

  const predictedTarget = maxTargetRef.current || (isActuallyFinishedCheck ? (nowForCalc - 1000) : (nowForCalc + MIN_DUR));
  
  // V6052: Prevent early loading screen blocking while Marc is still typing in the chat.
  // The loading screen will only mount once the simulated start time has actually passed.
  const isProcessStarted = !processStartTime || Date.now() >= processStartTime;
  
  const isTargetPending = predictedTarget > nowForCalc;
  const isGeneratingDiet = isProcessStarted && (status === 'generating_initial' || status === 'processing' || (status === 'completed' && needsDietReveal && isTargetPending && !timerFinishedLocally));
  const isGeneratingUpdate = isProcessStarted && (status === 'trigger_update' || status === 'updating');

  // V6035: Derive the reveal overlay state mathematically to prevent 1-frame flicker
  const missedLoadingScreen = missedLoadingScreenRef.current || ((!isGeneratingDiet && !isGeneratingUpdate) && (maxTargetRef.current !== null && Date.now() >= maxTargetRef.current));
  // V6052: Protect the reveal cinematic overlay from activating before the process start time has arrived.
  const showRevealOverlay = isProcessStarted && !hasDismissedReveal && needsDietReveal && (timerFinishedLocally || missedLoadingScreen);

  useEffect(() => {
     // V1004: Strictly require the visual timer to finish before showing the reveal cinematic and sending the message
     // V6031: If the user enters the screen and it's ALREADY completed, the loading screen won't render,
     // so timerFinishedLocally will never be true. We must auto-trigger if they missed the loading screen.
     if (showRevealOverlay) {
          // V2092: Accelerate Chat Message
          if (user) {
              supabase.from('chat_history')
               .select('id, content')
               .eq('user_id', user.id)
               .eq('role', 'model')
               .like('content', '%[DELAYED:%')
               .then(({ data: msgs }) => {
                 if (msgs && msgs.length > 0) {
                   const nowTime = Date.now();
                   const updates = msgs.map(msg => {
                     const match = msg.content.match(/\[DELAYED:([\d.]+)\]/);
                     if (match && match[1]) {
                       const scheduledTime = parseInt(match[1], 10);
                       if (scheduledTime > nowTime) {
                         const newContent = msg.content.replace(/\[DELAYED:[\d.]+\]/, `[DELAYED:${nowTime}]`);
                         // V6060: Write current local ISO timestamp to created_at to fix messages from the future!
                         return supabase.from('chat_history').update({ 
                           content: newContent,
                           created_at: new Date().toISOString()
                         }).eq('id', msg.id);
                       }
                     }
                     return null;
                   }).filter(Boolean);

                   if (updates.length > 0) {
                     Promise.all(updates).then(() => {
                       console.log("[Dashboard] Accelerated all future delayed model messages to now!");
                     });
                   }
                 }
               });
          }
     }
  }, [showRevealOverlay, user]);

  const isUpdateReveal = generationType === 'update' || (generationType !== 'new' && !!localStorage.getItem('aura_last_revealed_plan_id'));
  const [_, setTick] = useState(0);

  // Helper refs/state
  // V600: ROBUST PREMIUM CHECK (Prevents "Flash of Basic" on Reload)
  // We check localStorage directly to avoid waiting for AuthContext hydration.
  const isPremium = subscriptionStatus === 'active' ||
    subscriptionStatus === 'trialing' ||
    subscriptionStatus === 'pro' ||
    localStorage.getItem('aura_demo_mode') === 'true' ||
    localStorage.getItem('aura_subscription_status') === 'active' ||
    localStorage.getItem('aura_subscription_status') === 'pro' ||
    localStorage.getItem('aura_subscription_status') === 'trialing';

  // V705: Define block state when user is not premium or has pending photos
  const isBlockedState = !isPremium || status === 'pending_photos';

  // V425: MOCK FALLBACK for Basic Users
  const displayPlan = plan || ((!isPremium) ? PLACEHOLDER_PLAN : null);

  // V513: ROBUST PLAN VALIDATION - MOVED UP FOR TIMER LOGIC
  const hasValidPlan = plan && plan.comidas && plan.comidas.length > 0;

  // DEBUG: LOGGING FOR DIET FIX VERIFICATION
  useEffect(() => {
    if (user?.id) {
      console.log("--- DASHBOARD DEBUG ---");
      console.log("User ID:", user.id);
      console.log("Plan ID:", plan?.id);
      console.log("Plan Version:", plan?.version);
      console.log("Has Meals:", plan?.comidas?.length);
      console.log("Status:", status);
      console.log("Is Premium:", isPremium);
      console.log("Is AutoRecovering:", isPremium && !((plan && plan.comidas && plan.comidas.length > 0)) && !isGenerating && !hookLoading);
      if (plan?.resumen_diario?.personalization_tags) {
        console.log("Tags:", plan.resumen_diario.personalization_tags);
      }
      console.log("-----------------------");
    }
  }, [user, plan, status, isPremium, isGenerating, isSyncingCloud]);


  // Timer Logic
  const now = Date.now();
  const MIN_DURATION = (generationType === 'new' || status === 'generating_initial') ? 5400000 : 1800000;
  
  // Timer calculations removed because they are now handled globally above (lines 195-211)
  // We use the predictedTarget for the visual timer component as well.
  
  // V1400: FIX 100% PROGRESS FLASH
  // We use predictedTarget (calculated above) to maintain stability.
  const isActuallyFinished = status === 'completed' || status === 'error_generation' || status === 'error_finalization';
  const target = predictedTarget;

  const minutesLeft = Math.ceil(Math.max(0, target - now) / 60000);
  const isPerformingLoad = isGeneratingDiet || isGeneratingUpdate || isSyncingCloud;
  const processType = status;

  // Active Timer Ticker
  useEffect(() => {
    if (isGenerating && minutesLeft > 0) {
      const interval = setInterval(() => {
        setTick(t => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isGenerating, minutesLeft]);

  // V4000: DEAD MAN'S SWITCH (REMOVED)
  // The UI no longer forces an AI Timeout. We let the Global Worker handle the fallback gracefully.

  // Restore Option Selection
  useEffect(() => {
    const currentUserId = user?.id;
    if (currentUserId) {
      const saved = localStorage.getItem(`aura_selected_meal_options_${currentUserId}`);
      try { if (saved) setSelectedOptions(JSON.parse(saved)); } catch (e) { }
    }
  }, [user?.id]);

  const toggleMeal = (index: number) => setExpandedMeals(prev => ({ ...prev, [index]: !prev[index] }));
  const toggleStrategy = (index: number) => setExpandedStrategies(prev => ({ ...prev, [index]: !prev[index] }));

  // --- ACTIONS ---
  const handleOptionChange = (mealIdx: number, optionId: any) => {
    setSelectedOptions(prev => {
      const next = { ...prev, [mealIdx]: optionId };
      if (user?.id) localStorage.setItem(`aura_selected_meal_options_${user.id}`, JSON.stringify(next));
      return next;
    });
  };

  // handleManualSkip and aura_diet_error interception have been entirely removed.
  // We rely unconditionally on DietGlobalWorker to provide a valid fallback diet gracefully.
  // --- DERIVED LOADING STATE ---
  // We are "Loading" (Overlay visible) if:
  // 1. Hook is loading initial data
  // 2. Status is processing/generating

  // Note: We deliberately separate "hookLoading" (spinner) from "Diet Processing" (Timer Overlay)
  const loading = isGenerating;

  // Timer Logic Moved Up (V910)

  // User name fallback
  // V600: PERSONALIZATION FIX - Prioritize Questionnaire Name (LocalStorage) -> Auth Name -> Plan Name -> Default
  const getStoredUserData = () => {
    try {
      const stored = localStorage.getItem('aura_user_data');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn("Failed to parse aura_user_data", e);
    }
    return null;
  };

  const storedData = getStoredUserData() || {};
  const storedName = storedData.name;
  const authName = user?.user_metadata?.full_name || user?.user_metadata?.name;
  const userName = storedName || authName || (displayPlan as any)?.nombre_usuario || "Atleta Aura";

  // V605: STRATEGY SUBTITLE GENERATOR
  // V605: STRATEGY DETAILS GENERATOR
  const getStrategyDetails = () => {
    // Default fallback
    if (!storedData.goal) {
      return {
        goal: displayPlan?.resumen_diario?.objetivo_actual || 'Objetivo Aura',
        diet: displayPlan?.resumen_diario?.tipo_dia || 'Plan Balanceado',
        meals: '',
        tags: displayPlan?.resumen_diario?.personalization_tags || []
      };
    }

    const goalMap: Record<string, string> = {
      'fat': '🔥 Pérdida de Grasa',
      'fat_loss': '🔥 Pérdida de Grasa',
      'muscle': '💪 Ganancia Muscular',
      'performance': '⚡ Rendimiento Híbrido'
    };

    const dietMap: Record<string, string> = {
      'omnivore': '🍗 Omnívoro',
      'pescatarian': '🐟 Pescatariano',
      'vegetarian': '🥗 Vegetariano',
      'vegan': '🌱 Vegano'
    };

    // V660: DYNAMIC TAG PRIORITY
    let planTags = displayPlan?.resumen_diario?.personalization_tags || [];
    if (!Array.isArray(planTags)) {
      planTags = typeof planTags === 'string' ? [planTags] : [];
    }

    // Extract specific diet tag if present in AI response
    const dietTagFromPlan = planTags.find((t: string) =>
      t.includes('Vegan') || t.includes('Veget') || t.includes('Pesc') || t.includes('Omni') || t.includes('Carn')
    );

    let goal = goalMap[storedData.goal] || storedData.goal;


    // Map the raw diet preference or the tag to a clean display string
    let diet = dietMap[storedData.proteinPreference] || 'Omnívoro';

    // If AI gave us a specific tag, use it but try to map it to our format if possible, otherwise use raw
    if (dietTagFromPlan) {
      diet = dietTagFromPlan;
    }

    const meals = storedData.mealCount ? `🍽️ ${storedData.mealCount} Comidas` : '';

    return { goal, diet, meals, tags: planTags };
  };

  const strategyDetails = getStrategyDetails();


  const dailyTotals = React.useMemo(() => {
    if (!displayPlan || !displayPlan.comidas) return { calories: 0, protein: 0, carbs: 0, fats: 0 };

    let totalCals = 0;
    let totalProt = 0;
    let totalCarbs = 0;
    let totalFats = 0;

    (displayPlan as any).comidas?.forEach((meal: any, idx: number) => {
      const optionId = selectedOptions[idx];
      // V343: Robust selection with strict comparison
      const selectedOption = meal.opciones?.find((o: any) => {
        if (optionId == null) return false;
        return String(o.id) === String(optionId);
      }) || meal.opciones?.[0];

      // V347: AGGRESSIVE MACRO EXTRACTION
      // Priority: 1. macros object (English/Spanish), 2. Root legacy keys

      // V349: REGEX MACRO EXTRACTION (Handles "Total: 300kcal", "approx 20g")
      const parseMacro = (val: any) => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
          // Extract first number (integer or float with dot/comma)
          const match = val.match(/(\d+([.,]\d+)?)/);
          if (match) return parseFloat(match[0].replace(',', '.'));
        }
        return 0;
      };

      let cals = 0, prot = 0, carbs = 0, fats = 0;

      if (selectedOption?.macros) {
        // V348: ACCENTED KEYS SUPPORT (calorías, proteína)
        cals = parseMacro(selectedOption.macros.calories || selectedOption.macros.total_kal || selectedOption.macros.calorias || selectedOption.macros.calorías || selectedOption.macros.kcal || selectedOption.macros.kcals);
        prot = parseMacro(selectedOption.macros.protein || selectedOption.macros.proteina || selectedOption.macros.proteína || selectedOption.macros.proteinas);
        carbs = parseMacro(selectedOption.macros.carbs || selectedOption.macros.carbos || selectedOption.macros.carbohidratos);
        fats = parseMacro(selectedOption.macros.fats || selectedOption.macros.grasas || selectedOption.macros.grasa);
      }

      // Fallback to root legacy keys if 0
      if (!cals) cals = parseMacro(selectedOption?.kcal || selectedOption?.calories || 0);
      if (!prot) prot = parseMacro(selectedOption?.p || selectedOption?.protein || 0);
      if (!carbs) carbs = parseMacro(selectedOption?.c || selectedOption?.carbs || 0);
      if (!fats) fats = parseMacro(selectedOption?.f || selectedOption?.fats || 0);

      // console.log(`[DailyTotals] Meal ${idx} Option ${optionId}: Cals=${cals} P=${prot} C=${carbs} F=${fats}`);

      // V352: AUTO-CALCULATE CALORIES IF MISSING
      // If we have macros but no calories, derive them: (P*4) + (C*4) + (F*9)
      if (!cals && (prot || carbs || fats)) {
        cals = (prot * 4) + (carbs * 4) + (fats * 9);
      }

      totalCals += cals;
      totalProt += prot;
      totalCarbs += carbs;
      totalFats += fats;
    });

    // V350: ULTIMATE FALLBACK - Only if calculation failed AND summary has valid data
    if (totalCals < 10 && displayPlan?.resumen_diario) {

      const parseMacro = (val: any) => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
          const match = val.match(/(\d+([.,]\d+)?)/);
          if (match) return parseFloat(match[0].replace(',', '.'));
        }
        return 0;
      };

      const summaryCals = parseMacro(displayPlan.resumen_diario.total_kal);

      // V352: Only overwrite if summary has VALID data (>0)
      if (summaryCals > 0) {
        console.warn("[DailyTotals] Using Resumen Diario fallback (Valid Data Found).");
        totalCals = summaryCals;

        if ((displayPlan as any).resumen_diario.macros) {
          totalProt = parseMacro((displayPlan as any).resumen_diario.macros.proteina || (displayPlan as any).resumen_diario.macros.protein);
          totalCarbs = parseMacro((displayPlan as any).resumen_diario.macros.carbos || (displayPlan as any).resumen_diario.macros.carbs);
          totalFats = parseMacro((displayPlan as any).resumen_diario.macros.grasas || (displayPlan as any).resumen_diario.macros.fats);
        }
      }
    }

    return {
      calories: Math.round(totalCals),
      protein: Math.round(totalProt),
      carbs: Math.round(totalCarbs),
      fats: Math.round(totalFats)
    };
  }, [displayPlan, selectedOptions]);
  // V510: AUTO-RECOVERY EFFECT & ZOMBIE STATE HANDLING
  // If Premium + No Plan + Not Loading Hook + Not Generating -> FORCE GENERATION

  // V513: ROBUST PLAN VALIDATION (Moved up)
  // const hasValidPlan = plan && plan.comidas && plan.comidas.length > 0;

  // V510: AUTO-RECOVERY EFFECT & ZOMBIE STATE HANDLING
  // If Premium + Invalid Plan + Not Generating -> FORCE GENERATION (Even if previous state was error, we rely on local fallback to break loops)
  const hasPhotos = storedData.photos?.front && storedData.photos?.side && storedData.photos?.back;
  const isAutoRecovering = isPremium && hasPhotos && !hasValidPlan && !isGenerating && !isSyncingCloud && status !== 'pending_photos';

  useEffect(() => {
    if (isAutoRecovering && !showRevealOverlay) {
      console.log(`[Dashboard] Zombie State (Invalid Plan) Detected. Auto-triggering generation...`);
      // If we don't have a plan object AT ALL, it is a brand new generation.
      // If we have a malformed/empty plan object, we treat it as a recovery update.
      const isUpdate = !!plan;
      triggerGeneration(isUpdate).catch(e => console.error("Auto-trigger failed", e));
    }
  }, [isAutoRecovering, showRevealOverlay, triggerGeneration, plan]);

  // V1202: Reset the timer completion block when a new update starts
  useEffect(() => {
    if (status === 'trigger_update' || status === 'generating_initial' || status === 'processing' || status === 'updating') {
      setTimerFinishedLocally(false);
    }
  }, [status]);
  // V515: EFFECTIVELY LOADING LOGIC
  // If we are auto-recovering, show loading screen.
  // V545: SEPARATE INIT LOADING (Fixes Flash)
  // We removed (hookLoading && !hasValidPlan) from here. If we are just syncing, we show a generic spinner below.
  // V6033: Respect visual timer! Use the mathematically verified isGeneratingDiet variable
  const effectivelyLoading = (
    isAutoRecovering || 
    (isProcessStarted && (
      isGeneratingDiet || 
      isGeneratingUpdate || 
      status === 'error_generation' || 
      status === 'error_finalization' ||
      // V2109: Force loading state to stay alive if DB is done but visual timer is still ticking
      (status === 'completed' && needsDietReveal && !timerFinishedLocally && !missedLoadingScreen)
    ))
  );
  // --- EARLY RETURNS (MUST BE AFTER ALL HOOKS) ---
  // V630: PREVENT FLASH - Use Skeleton for Auth Loading or Hook Loading
  // V631: FIREWALL - Force unlock after 4 seconds to prevent indefinite skeleton traps for basic users
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceUnlockSkeleton(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  // V630: PREVENT FLASH - Use Skeleton for Auth Loading or Hook Loading
  // V634: Free users (isPremium === false) must NEVER see the Skeleton. They should instantly see the paywall.
  // V635: If we already have a cached plan, bypass the skeleton and show it immediately (SWR pattern)
  if (isPremium && (authLoading || (isSyncingCloud && !hasValidPlan)) && !effectivelyLoading && !forceUnlockSkeleton) {
    console.log("[NutritionDashboardScreen] Showing Skeleton due to authLoading/isSyncingCloud");
    return <NutritionDashboardSkeleton />;
  }

  // V545: GENERIC INITIALIZATION (No Flash of "AI Thinking")
  // If we are syncing with cloud and have no local cache, show simple spinner.
  // V546: BASIC USER FIX - Only show Syncing if Premium. Basic users get the Lock Screen via main render.
  if (isPremium && !hasValidPlan && !effectivelyLoading && !showRevealOverlay && !forceUnlockSkeleton) {
    console.log("[NutritionDashboardScreen] Showing Skeleton because !hasValidPlan");
    return <NutritionDashboardSkeleton />;
  }

  // V413: Removed 'Sincronizando' flash screen as requested by user.
  // It caused confusion for basic users and sometimes got stuck.


  // V419: PLAN READINESS GUARD & SELF-HEALING
  // If we have no plan but we ARE showing the reveal overlay, DO NOT show the error.
  if (!hasValidPlan && !isSyncingCloud && !effectivelyLoading && !showRevealOverlay) {
    if (isPremium && !forceUnlockSkeleton) {
      // V513: FORCE FLUID EXPERIENCE (User Request)
      // Si estamos aquí buscando recargar la dieta pero detecta que no hay,
      // en vez del cargador oscuro (pantallazo negro) usamos el Skeleton Premium fijo
      return <NutritionDashboardSkeleton />;
    }
  }



  // --- MAIN RENDER ---
  return (
    <div className="min-h-screen bg-background-dark text-white font-sans selection:bg-primary selection:text-black overflow-x-hidden relative">
      {/* V326 DEBUG HEADER - FULL TRACEABILITY */}


      {/* V328: BACKGROUND UPDATE BANNER REMOVED (V330) */}

      {/* SUCCESS REVEAL OVERLAY (Synergistic Transition) */}
      <AnimatePresence>
        {/* LOADING SCREEN OVERLAY - Fades out when showRevealOverlay becomes true */}
        {/* V431: Z-INDEX FIX: Restored to z-[90] so Menu (z-100) and Chat (z-110) are visible. */}
        {/* The Skip button inside must be moved to avoid BottomNav occlusion. */}
        {/* V2101: PREVENT FLASH OF LOADING SCREEN IF TIMER IS ALREADY FINISHED */}
        {effectivelyLoading && !showRevealOverlay && isPremium && (
          <motion.div
            key="loading-screen"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
            className="fixed inset-0 z-[90]"
          >
            {/* If the timer is already finished and we're just waiting for the cloud, show a simple loader to avoid the flash of the Loading Screen at 100% */}
            {Date.now() >= target && status !== 'completed' && !isSyncingCloud && !missedLoadingScreen && (
              <div className="absolute inset-0 bg-[#0a0a0c] z-50 flex flex-col items-center justify-center p-6 transition-opacity duration-300">
                <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50">Procesando...</span>
              </div>
            )}
            
            {/* We conditionally render NutritionLoadingScreen to prevent heavy GPU flashes on mobile Safari during the sync phase. */}
            {/* V3000: Removed isSyncingCloud from condition to prevent progress bar restart on background poll */}
            {!(missedLoadingScreen || (Date.now() >= target && status !== 'completed')) && (
              <NutritionLoadingScreen
                minutesLeft={minutesLeft}
                startTime={processStartTime || Date.now()}
                targetTime={target}
                onDevComplete={() => {
                  console.log("[Dashboard] Timer finished visually. Awaiting GlobalWorker graceful fallback if cloud is stuck...");
                  setTimerFinishedLocally(true);
                }}
                isPerformingLoad={isGeneratingDiet || isGeneratingUpdate || isSyncingCloud}
                isUpdate={!!hasValidPlan || generationType === 'update'}
                coachMessage={undefined}
              />
            )}
          </motion.div>
        )}

        {showRevealOverlay && isPremium && (
          <motion.div
            key="reveal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.9, filter: "blur(20px)" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center p-6"
          >
            {/* Cinematic Backdrop */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/20 via-black to-black opacity-80"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 pointer-events-none"></div>

            <div className="relative z-10 text-center space-y-8 max-w-sm mx-auto">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full backdrop-blur-md shadow-[0_0_20px_rgba(242,127,13,0.2)]"
              >
                <Sparkles className="w-3 h-3 text-primary animate-pulse" />
                <span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">{t('nutrition.optimized_protocol', 'Protocolo Optimizado')}</span>
              </motion.div>

              {/* Marc's Presence */}
              <div className="relative group">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="size-32 rounded-full overflow-hidden border-2 border-primary/50 shadow-[0_0_50px_rgba(242,127,13,0.4)] mx-auto relative z-10"
                >
                  <img
                    src={marcAvatarUrl || "/assets/images/landing/marc.jpg"}
                    alt="Marc Serrano"
                    className="w-full h-full object-cover object-[center_20%]"
                  />
                </motion.div>
                {/* Rotating Rings */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-40 border border-primary/20 rounded-full animate-[spin_10s_linear_infinite]"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-44 border border-white/5 rounded-full animate-[spin_15s_linear_infinite_reverse]"></div>
              </div>

              <div className="space-y-4">
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-4xl font-black uppercase text-white tracking-tighter leading-none drop-shadow-2xl"
                >
                  {isUpdateReveal ? (
                    <>
                      {t('nutrition.marc_has_updated')}<br />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-white to-green-400 animate-premium-shine pr-2">{t('nutrition.updated_diet')}</span>
                    </>
                  ) : (
                    <>
                      {t('nutrition.marc_has_created')}<br />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-white to-primary animate-premium-shine pr-2">{t('nutrition.created_diet')}</span>
                    </>
                  )}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="text-[11px] font-bold text-white/50 uppercase tracking-widest leading-relaxed max-w-[280px] mx-auto"
                >
                  {isUpdateReveal 
                    ? t('nutrition.profile_analyzed_update', { name: userName.split(' ')[0] }) 
                    : t('nutrition.profile_analyzed_new', { name: userName.split(' ')[0] })}
                </motion.p>
              </div>

              {/* Value Highlights */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 }}
                className="grid grid-cols-2 gap-3"
              >
                <div className="bg-white/5 border border-white/10 p-3 rounded-2xl backdrop-blur-sm text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="size-2 bg-primary rounded-full"></div>
                    <span className="text-[8px] font-black text-primary uppercase tracking-widest">{t('nutrition.goal_label')}</span>
                  </div>
                  <p className="text-[10px] font-bold text-white uppercase italic">{t('nutrition.max_hypertrophy')}</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-3 rounded-2xl backdrop-blur-sm text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="size-2 bg-green-500 rounded-full"></div>
                    <span className="text-[8px] font-black text-green-500 uppercase tracking-widest">{t('nutrition.status_label')}</span>
                  </div>
                  <p className="text-[10px] font-bold text-white uppercase italic">{t('nutrition.analysis_100')}</p>
                </div>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={async () => {
                  if (!plan) {
                    console.warn("[Dashboard] Reveal Clicked but PLAN is NULL. Holding transition.");
                    return;
                  }
                  setRevealDismissedState(true);
                  setTimerFinishedLocally(true);
                  

                  // V6039: Guard against double-clicks
                  if (sessionStorage.getItem('aura_diet_chat_revealed') === 'true') {
                     return;
                  }
                  sessionStorage.setItem('aura_diet_chat_revealed', 'true');
                  
                  // V6042: Save to local storage IMMEDIATELY to prevent read-replica ghost popups
                  if (plan) {
                    const currentPlanId = plan.generatedDate || plan.id;
                    if (currentPlanId) {
                      localStorage.setItem('aura_last_revealed_plan_id', currentPlanId);
                    }
                  }
                  
                  // V6038: Await clearDietReveal BEFORE dispatching the event!
                  await clearDietReveal();
                  window.dispatchEvent(new Event('aura_diet_updated'));
                }}
                disabled={!plan}
                className="relative cursor-pointer overflow-hidden w-full bg-neutral-900/90 backdrop-blur-xl text-white font-black uppercase tracking-[0.2em] h-[72px] rounded-2xl shadow-[0_10px_40px_rgba(242,127,13,0.2)] hover:shadow-[0_20px_60px_rgba(242,127,13,0.4)] transition-all flex items-center justify-center gap-3 text-[10px] border border-primary/30 group/btn disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* Pulsing Aura Border */}
                <div className="absolute inset-0 rounded-2xl border-2 border-primary/20 animate-pulse-slow pointer-events-none"></div>

                {/* Shimmer Sweep (Amber/Orange) */}
                <motion.div
                  animate={{
                    left: ['-100%', '200%'],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeInOut"
                  }}
                  className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-primary/30 to-transparent skew-x-[30deg] pointer-events-none"
                />

                <motion.span
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  className="material-symbols-outlined font-bold text-primary group-hover/btn:animate-bounce"
                >
                  rocket_launch
                </motion.span>
                <span className="relative z-10 bg-gradient-to-r from-white via-white to-primary/50 bg-clip-text text-transparent">
                  {!plan ? t('nutrition.loading_data') : (isUpdateReveal ? t('nutrition.view_changes') : t('nutrition.access_diet'))}
                </span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="pb-28 md:pb-32 relative overflow-x-hidden">

        {/* PENDING PHOTOS LOCK OVERLAY */}
        {isPremium && status === 'pending_photos' && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-start pt-[14vh] p-6 bg-black/85 backdrop-blur-md pointer-events-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="relative overflow-hidden bg-gradient-to-b from-[#0a0a0c] via-[#050507] to-[#020203] border-t border-white/10 border-x border-white/5 border-b border-black/50 rounded-[2rem] p-10 max-w-sm w-full shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9),inset_0_4px_12px_rgba(255,255,255,0.02)] text-center group overscroll-contain"
            >
              {/* Soft Orange Glow Accent */}
              <div className="absolute -right-20 -bottom-25 w-72 h-72 rounded-full bg-primary/10 blur-[80px] pointer-events-none" />

              {/* Glowing Camera Icon with Pulse LED */}
              <div className="size-20 bg-gradient-to-b from-[#1b1b1f] to-black rounded-full flex items-center justify-center mx-auto mb-8 border border-white/10 shadow-[inset_0_4px_10px_rgba(0,0,0,0.85),0_0_20px_rgba(242,127,13,0.15)] relative">
                <span className="material-symbols-outlined text-3xl text-primary drop-shadow-[0_0_10px_rgba(242,127,13,0.3)]">add_a_photo</span>
                <span className="absolute -top-1 -right-1 size-3 rounded-full bg-primary animate-pulse shadow-[0_0_10px_#f27f0d]"></span>
              </div>

              <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-wider mb-3 leading-tight">
                {t('nutrition.pending_photos_title', 'FOTOS CORPORALES')}
              </h2>
              <p className="text-[10px] text-white/50 tracking-wide mb-8 max-w-[280px] mx-auto leading-relaxed font-medium">
                {t('nutrition.pending_photos_desc', 'Marc Serrano necesita tus fotos corporales (frontal, lateral y trasera) para calcular tu plan de nutrición premium.')}
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => setIsPhotosModalOpen(true)}
                  className="w-full bg-gradient-to-r from-primary to-primary-hover text-white font-black uppercase tracking-widest py-4 rounded-xl border border-t-white/15 border-x-transparent border-b-black/30 shadow-[0_10px_30px_rgba(242,127,13,0.2)] hover:shadow-[0_15px_40px_rgba(242,127,13,0.35)] transition-all flex items-center justify-center gap-2 text-[10px] hover:scale-[1.02] active:scale-[0.98]"
                >
                  <span className="material-symbols-outlined text-sm">photo_camera</span>
                  <span>{t('nutrition.upload_photos_button', 'SUBIR FOTOS AHORA')}</span>
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-neutral-950 text-white/40 font-black uppercase tracking-widest py-4 rounded-xl border border-white/5 hover:border-white/10 hover:text-white/70 transition-all text-[9px]"
                >
                  {t('nutrition.back_to_home')}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* LOCAL LOCK OVERLAY (Only for basic users) */}
        {!isPremium && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-start pt-[14vh] p-6 bg-black/85 backdrop-blur-md pointer-events-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="relative overflow-hidden bg-gradient-to-b from-[#0a0a0c] via-[#050507] to-[#020203] border-t border-white/10 border-x border-white/5 border-b border-black/50 rounded-[2rem] p-10 max-w-sm w-full shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9),inset_0_4px_12px_rgba(255,255,255,0.02)] text-center group overscroll-contain"
            >
              {/* Soft Gold Glow Accent */}
              <div className="absolute -right-20 -bottom-25 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-[80px] pointer-events-none" />

              {/* Glowing Lock Icon */}
              <div className="size-20 bg-gradient-to-b from-[#1b1b1f] to-black rounded-full flex items-center justify-center mx-auto mb-8 border border-[#D4AF37]/35 shadow-[inset_0_4px_10px_rgba(0,0,0,0.85),0_0_20px_rgba(212,175,55,0.15)] relative">
                <span className="material-symbols-outlined text-3xl text-[#D4AF37] drop-shadow-[0_0_10px_rgba(212,175,55,0.3)]">workspace_premium</span>
              </div>

              <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-wider mb-3 leading-tight">
                {t('nutrition.elite_plan')}
              </h2>
              <p className="text-[10px] text-white/50 tracking-wide mb-8 max-w-[280px] mx-auto leading-relaxed font-medium">
                {t('nutrition.elite_plan_desc')}
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-gradient-to-r from-primary to-[#D4AF37] text-white font-black uppercase tracking-widest py-4 rounded-xl border border-t-white/15 border-x-transparent border-b-black/30 shadow-[0_10px_30px_rgba(242,127,13,0.2)] hover:shadow-[0_15px_40px_rgba(242,127,13,0.35)] transition-all flex items-center justify-center gap-2 text-[10px] hover:scale-[1.02] active:scale-[0.98]"
                >
                  <span>{t('nutrition.unlock_free_trial')}</span>
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-neutral-950 text-white/40 font-black uppercase tracking-widest py-4 rounded-xl border border-white/5 hover:border-white/10 hover:text-white/70 transition-all text-[9px]"
                >
                  {t('nutrition.back_to_home')}
                </button>
              </div>
            </motion.div>
          </div>
        )}


        {/* HEADER */}
        <header className={`relative z-10 px-6 pt-safe-top-large mb-8 transition-all duration-700 ${isBlockedState || effectivelyLoading ? 'blur-[8px] opacity-0 pointer-events-none scale-95' : ''}`}>
          <div className="max-w-4xl mx-auto">
            
            {/* V670: MINIMALIST DIET SPECS STRIP */}
            <p className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block mr-2 animate-pulse shadow-[0_0_8px_orange] align-middle -translate-y-[1px]"></span>
              {t('nutrition.title_nutricion', 'Nutrición')} • {t('nutrition.active_protocol', 'Protocolo Activo')} • {strategyDetails.goal.replace(/^[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]*/, '')} • {strategyDetails.diet.replace(/^[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]*/, '')} {strategyDetails.meals && `• ${strategyDetails.meals.replace(/^[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]*/, '')}`}
              {strategyDetails.tags
                .filter((tag: string) => {
                  const normalize = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                  const t = normalize(tag);
                  const currentDiet = normalize(strategyDetails.diet);
                  const currentGoal = normalize(strategyDetails.goal);

                  return !t.includes('sincronizacion') &&
                    !t.includes('nutrient timing') &&
                    !t.includes('vegan') &&
                    !t.includes('veget') &&
                    !t.includes('pesc') &&
                    !t.includes('omni') &&
                    !t.includes('carn') &&
                    !t.includes(currentDiet) &&
                    !currentDiet.includes(t) &&
                    !t.includes(currentGoal) &&
                    !currentGoal.includes(t);
                })
                .map((tag: string) => ` • ${tag.replace(/^[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]*/, '')}`)
                .join('')}
            </p>
          </div>
        </header>





        {/* UNIFIED MACRO HEADER - MINIMALIST TEXT STRIP */}
        <div className={`px-6 mb-10 relative z-10 transition-all duration-700 ${!isPremium || effectivelyLoading ? 'blur-[8px] opacity-50 pointer-events-none scale-95' : ''}`}>
          <div className="flex items-center justify-around py-4 border-y border-white/5 bg-transparent max-w-xl mx-auto w-full">
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-white/40 uppercase font-black tracking-[0.15em] mb-0.5">{t('nutrition.kcal_label')}</span>
              <span className="text-base font-black text-white">{dailyTotals.calories}</span>
            </div>
            <div className="h-6 w-px bg-white/10"></div>
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-green-500/80 uppercase font-black tracking-[0.15em] mb-0.5">{t('nutrition.prot_label')}</span>
              <span className="text-base font-black text-white">{dailyTotals.protein}g</span>
            </div>
            <div className="h-6 w-px bg-white/10"></div>
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-blue-400/80 uppercase font-black tracking-[0.15em] mb-0.5">{t('nutrition.carb_label')}</span>
              <span className="text-base font-black text-white">{dailyTotals.carbs}g</span>
            </div>
            <div className="h-6 w-px bg-white/10"></div>
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-yellow-500/80 uppercase font-black tracking-[0.15em] mb-0.5">{t('nutrition.fat_label')}</span>
              <span className="text-base font-black text-white">{dailyTotals.fats}g</span>
            </div>
          </div>
        </div>

        {/* MEAL LIST */}
        {/* MEAL LIST */}
        <div className={`px-6 relative z-10 transition-all duration-700 ${!isPremium || showRevealOverlay || effectivelyLoading ? 'blur-[8px] opacity-20 pointer-events-none scale-90 translate-y-4' : 'opacity-100'}`}>

          {/* V433: EMPTY STATE RESCUE - Checks if plan is missing or EMPTY despite being "Loaded" */}
          {/* V513: Only show this error for BASIC users. Premium users get auto-regenerated upstream. */}
          {!isPremium && !loading && (!displayPlan || (displayPlan?.comidas && displayPlan.comidas.length === 0)) && (
            <div className="p-8 py-12 text-center rounded-3xl bg-red-500/10 border border-red-500/20 backdrop-blur-xl mb-6">
              <span className="material-symbols-outlined text-4xl text-red-400 mb-4">gpp_maybe</span>
              <h3 className="text-xl font-bold text-white mb-2">{t('nutrition.plan_not_found')}</h3>
              <p className="text-sm text-white/60 mb-6">{t('nutrition.plan_sync_error')}</p>
              <button
                onClick={() => {
                  localStorage.setItem('aura_diet_status', 'trigger_update');
                  window.location.reload();
                }}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-colors uppercase tracking-widest text-xs"
              >
                {t('nutrition.regenerate_diet')}
              </button>
            </div>
          )}

          {displayPlan?.comidas && displayPlan.comidas.length > 0 && (
            <div className="relative max-w-xl mx-auto w-full bg-gradient-to-b from-[#010102] via-[#020203] to-[#040405] border-t border-white/5 border-x border-white/[0.03] border-b border-white/10 rounded-[2rem] px-5 py-2 sm:px-6 shadow-[0_8px_30px_rgba(242,127,13,0.12),inset_0_6px_18px_rgba(0,0,0,0.95)] overflow-hidden">
              {/* Premium Glow Decoration */}
              <div className="absolute -top-32 -right-32 size-64 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
              <div className="absolute -bottom-32 -left-32 size-64 bg-orange-600/5 rounded-full blur-[100px] pointer-events-none" />

              <div className="relative z-10 divide-y divide-white/5">
                {
                  displayPlan.comidas.map((meal, mIdx) => {
                    const isExpanded = !!expandedMeals[mIdx];
                    const currentOptionId = selectedOptions[mIdx];
                    // V343: Robust selection with strict comparison
                    const currentOption = meal.opciones?.find(o => {
                      if (currentOptionId == null) return false;
                      return String(o.id) === String(currentOptionId);
                    }) || meal.opciones?.[0];

                    if (!currentOption) return null;

                    const parseMacro = (val: any) => {
                      if (typeof val === 'number') return val;
                      if (typeof val === 'string') {
                        const match = val.match(/(\d+([.,]\d+)?)/);
                        if (match) return parseFloat(match[0].replace(',', '.'));
                      }
                      return 0;
                    };

                    const cOpt = currentOption as any;
                    let cals = 0, prot = 0, carbs = 0, fats = 0;
                    if (cOpt?.macros) {
                      cals = parseMacro(cOpt.macros.calories || cOpt.macros.total_kal || cOpt.macros.calorias || cOpt.macros.calorías || cOpt.macros.kcal || cOpt.macros.kcals);
                      prot = parseMacro(cOpt.macros.protein || cOpt.macros.proteina || cOpt.macros.proteína || cOpt.macros.proteinas);
                      carbs = parseMacro(cOpt.macros.carbs || cOpt.macros.carbos || cOpt.macros.carbohidratos);
                      fats = parseMacro(cOpt.macros.fats || cOpt.macros.grasas || cOpt.macros.grasa);
                    }
                    if (!cals) cals = parseMacro(cOpt?.kcal || cOpt?.calories || 0);
                    if (!prot) prot = parseMacro(cOpt?.p || cOpt?.protein || 0);
                    if (!carbs) carbs = parseMacro(cOpt?.c || cOpt?.carbs || 0);
                    if (!fats) fats = parseMacro(cOpt?.f || cOpt?.fats || 0);
                    if (!cals && (prot || carbs || fats)) {
                      cals = (prot * 4) + (carbs * 4) + (fats * 9);
                    }
                    const hasMacros = cals > 0 || prot > 0 || carbs > 0 || fats > 0;

                    const mealKey = `m${mIdx + 1}`;
                    const isCompleted = completedMeals.includes(mealKey);

                    return (
                      <div
                        key={mIdx}
                        className={`relative transition-all duration-500 py-4 sm:py-5 ${
                          isCompleted ? 'opacity-55' : 'opacity-100'
                        }`}
                      >
                        {/* MEAL HEADER */}
                        <button
                          onClick={() => toggleMeal(mIdx)}
                          className="w-full flex items-center justify-between text-left relative z-10 group gap-3"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* CHEVRON LEFT */}
                            <div className={`size-7 rounded-full border border-white/10 flex items-center justify-center transition-all duration-300 flex-shrink-0 ${isExpanded ? 'bg-white text-black rotate-180' : 'bg-transparent text-white/40 hover:bg-white/5'}`}>
                              <span className="material-symbols-outlined text-xs">expand_more</span>
                            </div>

                            <div className="flex flex-col min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                <span className="text-[10px] font-black uppercase text-primary tracking-widest">{String(meal.horario_sugerido || '')}</span>
                                <span className="size-1 bg-white/20 rounded-full"></span>
                                <span className="text-[9px] text-text-muted uppercase tracking-wider">{String(meal.etiqueta_timing || '')}</span>
                              </div>
                              <h3 className={`font-black uppercase text-[15px] leading-[1.1] sm:text-base tracking-tight transition-all duration-300 ${
                                isCompleted
                                  ? 'text-white/30 line-through decoration-white/20'
                                  : 'text-white group-hover:text-primary'
                              }`}>{String(meal.nombre_comida || '')}</h3>
                              <p className={`text-[11px] mt-0.5 transition-all duration-300 ${isCompleted ? 'text-white/20' : 'text-white/60'} truncate`}>
                                {currentOption?.nombre_plato || t('nutrition.standard_option')}
                              </p>
                            </div>
                          </div>

                          {/* CHECKBOX RIGHT */}
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMealCompletion(mealKey);
                            }}
                            className={`size-5 sm:size-6 rounded-full border flex items-center justify-center transition-all duration-300 cursor-pointer flex-shrink-0 ${
                              isCompleted
                                ? 'bg-[#D4AF37] border-[#D4AF37] text-black shadow-[0_0_10px_rgba(212,175,55,0.4)]'
                                : 'bg-white/5 border-white/10 text-white/40 hover:border-white/40'
                            }`}
                          >
                            {isCompleted ? (
                              <span className="material-symbols-outlined text-[12px] sm:text-[14px] font-black">check</span>
                            ) : (
                              <span className="size-1.5 sm:size-2 rounded-full bg-white/20 hover:bg-white/40"></span>
                            )}
                          </div>
                        </button>

                        {/* MEAL CONTENT */}
                        {isExpanded && (
                          <div className="pb-2 pt-4 mt-4 border-t border-white/5 slide-in-from-top-4 duration-500 relative z-10 pl-8 sm:pl-10">

                        {/* OPTION TABS */}
                        <div className="flex gap-6 mb-4 max-w-xs sm:max-w-sm border-b border-white/5 pb-2">
                          {meal.opciones?.map((opt, oIdx) => {
                            const isActive = (currentOptionId != null)
                              ? (String(opt.id) === String(currentOptionId))
                              : (oIdx === 0);

                            return (
                              <button
                                key={`${mIdx}-${opt.id}`}
                                onClick={() => handleOptionChange(mIdx, opt.id)}
                                className={`pb-2 text-[10px] font-black uppercase tracking-widest transition-all relative z-20 border-b-2 -mb-[9px] ${isActive
                                  ? 'text-white border-primary'
                                  : 'text-white/30 hover:text-white/60 border-transparent'
                                  }`}
                              >
                                {t('nutrition.option')} {String.fromCharCode(65 + oIdx)}
                              </button>
                            );
                          })}
                        </div>

                        {/* DISH DETAILS */}
                        <div className="space-y-6 max-w-xl">
                          {hasMacros && (
                            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] font-bold tracking-wider text-white/40 uppercase mb-4 mt-2">
                              <div><span className="text-white font-black">{Math.round(cals)}</span> <span className="text-[8px] font-black text-white/40">KCAL</span></div>
                              <span className="text-white/10 font-normal">·</span>
                              <div><span className="text-white font-black">{Math.round(prot)}g</span> <span className="text-[8px] font-black text-white/40">PROT</span></div>
                              <span className="text-white/10 font-normal">·</span>
                              <div><span className="text-white font-black">{Math.round(carbs)}g</span> <span className="text-[8px] font-black text-white/40">CARB</span></div>
                              <span className="text-white/10 font-normal">·</span>
                              <div><span className="text-white font-black">{Math.round(fats)}g</span> <span className="text-[8px] font-black text-white/40">GRASA</span></div>
                            </div>
                          )}
                          


                          {/* RAW INGREDIENTS */}
                          <div className="border-t border-white/5 pt-6 mt-6">
                            <h4 className="text-[10px] font-black text-text-muted uppercase tracking-[0.25em] mb-4 flex items-center gap-2">
                              <span className="material-symbols-outlined text-sm">kitchen</span>
                              {t('nutrition.ingredients_raw')}
                            </h4>
                            <ul className="space-y-1">
                              {Array.isArray(currentOption.ingredientes) && currentOption.ingredientes.map((ing: any, iIdx: number) => {
                                let ingName: string | null = null;
                                let displayQuantity: string | number | null = ing.gramos_crudo;

                                if (ing && typeof ing === 'object' && typeof ing.alimento === 'string') {
                                  ingName = ing.alimento;
                                } else if (typeof ing === 'string') {
                                  ingName = ing;
                                }

                                if (!ingName || ingName.includes("REDUCIR GRASAS")) return null;

                                if (!displayQuantity && ingName) {
                                  const qtyMatch = ingName.match(/(\(?\d+(?:[.,]\d+)?\s*(?:g|ml|oz|ud|unidad|loncha|pieza|taza|cucharada|rebanada|scoop|cazo)s?\)?)/i);
                                  if (qtyMatch) {
                                    let rawQty = qtyMatch[1];
                                    if (rawQty.startsWith('(') && rawQty.endsWith(')')) {
                                      rawQty = rawQty.slice(1, -1);
                                    }

                                    displayQuantity = rawQty;
                                    ingName = ingName.replace(qtyMatch[0], '').replace('()', '').trim();

                                    if (ingName.toLowerCase().startsWith('de ')) ingName = ingName.substring(3).trim();
                                    if (ingName.toLowerCase().endsWith(' de')) ingName = ingName.slice(0, -3).trim();
                                  }
                                }

                                return (
                                  <li key={iIdx} className="flex items-center py-3 border-b border-white/5 last:border-0 group/ing hover:bg-white/5 px-2 rounded-lg transition-colors">
                                    <div className="flex items-center gap-4 w-full">
                                      <div className="-ml-6 flex-shrink-0 text-white/30 group-hover/ing:text-primary transition-colors duration-200">
                                        {getIngredientIcon(ingName, "w-4 h-4 text-current")}
                                      </div>

                                      {/* QUANTITY FIRST (OPTION C + A) */}
                                      {(displayQuantity || (ing?.gramos_crudo != null && ['string', 'number'].includes(typeof ing.gramos_crudo))) && (
                                        <span className="text-xs font-black text-primary tabular-nums whitespace-nowrap min-w-[50px] sm:min-w-[60px] text-left">
                                          {(() => {
                                            if (displayQuantity && typeof displayQuantity === 'string' && (displayQuantity.includes('g') || displayQuantity.includes('ml') || displayQuantity.includes('ud'))) {
                                              return displayQuantity;
                                            }

                                            const n = ingName?.toLowerCase() || '';
                                            const rawStr = String(displayQuantity || ing?.gramos_crudo || '').replace(',', '.');
                                            const rawVal = parseFloat(rawStr) || 0;
                                            const qStr = rawStr.toLowerCase();

                                            if (qStr.includes('ml') || qStr.includes('g') || qStr.includes('oz') || qStr.includes('ud')) {
                                              return qStr;
                                            }

                                            if (n.includes('huevo')) {
                                              const count = Math.max(1, Math.round(rawVal / 55));
                                              return `${count} ud`;
                                            }
                                            if (n.includes('rebanada') || n.includes('tostada')) {
                                              const count = Math.max(1, Math.round(rawVal / 28));
                                              return `${count} ud`;
                                            }
                                            if (n.includes('tortita')) {
                                              const count = Math.max(1, Math.round(rawVal / 40));
                                              return `${count} ud`;
                                            }
                                            if (n.includes('scoop')) {
                                              const count = Math.round((rawVal / 30) * 10) / 10;
                                              return `${count} scoop${count !== 1 ? 's' : ''}`;
                                            }
                                            if (n.includes('manzana') || n.includes('plátano') || n.includes('naranja') || n.includes('fruta')) {
                                              if (rawVal < 200) return "1 ud";
                                              return `${Math.round(rawVal / 150)} ud`;
                                            }

                                            if (n.includes('proteína') || n.includes('protein') || n.includes('whey') || n.includes('suero') || n.includes('polvo') || n.includes('cacao') || n.includes('harina')) {
                                              return `${rawVal}g`;
                                            }

                                            if (n.includes('leche') || n.includes('lácteo') || n.includes('bebida') || n.includes('agua') || n.includes('zumo') || n.includes('caldo') || n.includes('kéfir')) {
                                              return `${rawVal}ml`;
                                            }

                                            return `${rawVal}g`;
                                          })()}
                                        </span>
                                      )}

                                      <div className="flex flex-col justify-center min-w-0">
                                        <span className="text-sm font-bold text-white/90 truncate">{ingName.charAt(0).toUpperCase() + ingName.slice(1)}</span>
                                        {ing?.nota && (
                                          <span className="text-[10px] text-white/40 font-medium uppercase tracking-wide truncate">
                                            {typeof ing.nota === 'string' ? ing.nota : String(ing.nota)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>

                          {/* COACH METHODOLOGY STRATEGY (STATIC BLOCK) */}
                          {meal.explicacion_timing && (
                            <div className="border-t border-white/5 pt-6 mt-6">
                              <div className={`border-l-2 pl-4 py-1 relative overflow-hidden ${String(meal.insight_valor || '').includes('STACK:') ? 'border-red-500/50 bg-red-950/10 rounded-r-xl p-3 border-y border-r border-red-500/10' : 'border-primary/30'}`}>
                                <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${String(meal.insight_valor || '').includes('STACK:') ? 'text-red-400' : 'text-primary'}`}>
                                  {String(meal.insight_valor || '').includes('STACK:') ? 'ERROR CRÍTICO' : t('nutrition.aura_strategy')}
                                </p>
                                <p className="text-xs text-white/70 leading-relaxed font-bold break-words">
                                  {String(meal.explicacion_timing || '')}
                                </p>
                                {meal.insight_valor && (
                                  <p className="text-[10px] text-white/40 leading-relaxed font-medium italic mt-1.5 break-words">
                                    {String(meal.insight_valor || '')}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

        {/* SUPPLEMENTS */}
        {(displayPlan?.supplements?.length > 0 || displayPlan?.suplementacion?.length > 0) && (
          <div className={`px-6 mt-5 relative z-10 transition-all duration-700 ${!isPremium || effectivelyLoading ? 'blur-[8px] opacity-20 scale-95 pointer-events-none' : 'opacity-100'}`}>
            <div className="max-w-xl mx-auto">
              
              {/* Unified Premium Master Card Container - Gold Highlight (Option 1) */}
              <div className="w-full rounded-2xl bg-gradient-to-r from-[#0c0c0e]/80 via-[#0c0c0e]/30 to-transparent border border-[#D4AF37]/15 hover:border-[#D4AF37]/30 shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_25px_rgba(212,175,55,0.03)] transition-all duration-300 overflow-hidden">
                
                {/* Header Clickable Row */}
                <button
                  onClick={() => setIsSupplementsSectionExpanded(!isSupplementsSectionExpanded)}
                  className="w-full flex items-center gap-3.5 p-4 text-left group hover:bg-white/[0.02] transition-colors duration-300 select-none"
                >
                  <div className={`size-8 rounded-full border flex items-center justify-center transition-all duration-300 flex-shrink-0 ${isSupplementsSectionExpanded ? 'bg-white text-black rotate-180 border-white' : 'bg-transparent text-white/40 hover:bg-white/5 border-white/10'}`}>
                    <span className="material-symbols-outlined text-sm">expand_more</span>
                  </div>

                  <div className="flex flex-col min-w-0">
                    <span className="text-[9px] font-black uppercase text-primary tracking-widest mb-0.5">Aura Suplementación</span>
                    <h2 className="text-base sm:text-lg font-black uppercase tracking-tight text-white group-hover:text-primary transition-colors leading-tight">
                      Suplementación Esencial
                    </h2>
                  </div>
                </button>

                <AnimatePresence>
                  {isSupplementsSectionExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "circOut" }}
                      className="overflow-hidden px-4 pb-4 border-t border-white/5"
                    >
                      
                      {/* AFFILIATE LINK CTA - REDESIGNED PREMIUM VERSION */}
                      <div className="py-4">
                        <a
                          href="http://bigsuplements.myshopify.com/discount/MARC?redirect=%2F%3Fafmc%3DMARC%26utm_campaign%3DMARC%26utm_source%3Dleaddyno%26utm_medium%3Daffiliate"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-4 w-full p-3.5 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/45 transition-all duration-300 group/btn shadow-[0_4px_25px_rgba(212,175,55,0.04)] hover:shadow-[0_4px_30px_rgba(212,175,55,0.08)] hover:scale-[1.01] active:scale-95"
                        >
                          {/* Cart Icon & 10% DTO Badge container */}
                          <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-[#D4AF37] text-lg shrink-0 animate-pulse">shopping_cart</span>
                            <div className="bg-[#D4AF37] text-black font-black text-[9px] px-2 py-1 rounded-md tracking-wider leading-none shadow-[0_0_10px_rgba(212,175,55,0.3)] uppercase">
                              10% Dto
                            </div>
                          </div>

                          {/* Text titles */}
                          <div className="flex flex-col items-start gap-0.5 min-w-0">
                            <span className="text-[10px] font-black uppercase tracking-wider text-white leading-none">
                              Consigue tu suplementación
                            </span>
                            <span className="text-[8px] font-bold text-white/50 uppercase tracking-wider leading-none flex items-center gap-1 mt-0.5">
                              {t('nutrition.discount_code')} 
                              <span className="text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/20 px-1 py-0.5 rounded text-[8px] font-black leading-none uppercase">
                                MARC
                              </span>
                            </span>
                          </div>

                          {/* Arrow right */}
                          <span className="material-symbols-outlined text-white/40 text-xs group-hover/btn:translate-x-0.5 transition-transform shrink-0 ml-auto">arrow_outward</span>
                        </a>
                      </div>

                      {/* SUPPLEMENTS LIST */}
                      <div className="flex flex-col mt-2 space-y-4 pb-4">
                        {(() => {
                          let sups = displayPlan.suplementacion?.length > 0 ? displayPlan.suplementacion : displayPlan.supplements;
                          if (sups && !Array.isArray(sups)) sups = [sups];
                          return (sups || []).map((s: any, idx: number) => {
                            const isExpanded = expandedSupplement === idx;
                            const supKey = `s${idx + 1}`;
                            const isCompleted = completedMeals.includes(supKey);

                            const getFallbackExplanation = (name: string) => {
                              const n = name.toLowerCase();
                              const isEn = i18n.language?.startsWith('en');
                              
                              if (n.includes('creatinal') || n.includes('creatina')) return isEn 
                                ? "Saturates intramuscular phosphocreatine stores, delaying central nervous system fatigue and allowing 1-2 extra reps on heavy sets to maximize hypertrophy." 
                                : "Satura los depósitos de fosfocreatina para ganar fuerza explosiva, mejorar la recuperación entre series y promover la retención de agua intracelular dando un aspecto más rocoso.";
                              if (n.includes('whey') || n.includes('proteína') || n.includes('protein')) return isEn 
                                ? "Provides an extremely fast supply of essential amino acids to halt catabolism and ignite protein synthesis, repairing muscle fibers micro-torn during training." 
                                : "Proporciona un aporte rapidísimo de aminoácidos esenciales para detener el catabolismo y encender la síntesis proteica, reparando las fibras musculares micro-rotas durante el entrenamiento.";
                              if (n.includes('omega') || n.includes('3')) return isEn 
                                ? "Acts as a natural systemic anti-inflammatory. Improves insulin sensitivity facilitating nutrients to go to muscle instead of fat, while protecting cardiovascular health." 
                                : "Actúa como un antiinflamatorio sistémico natural. Mejora la sensibilidad a la insulina facilitando que los nutrientes vayan al músculo y no a la grasa, además de proteger la salud cardiovascular.";
                              if (n.includes('multi')) return isEn 
                                ? "Nutritional life insurance. Ensures no metabolic pathway stalls due to lack of vitamins or minerals, optimizing the assimilation of your diet's macros." 
                                : "Seguro de vida nutricional. Asegura que ninguna ruta metabólica se estanque por falta de vitaminas o minerales, optimizando la asimilación de los macros de tu dieta.";
                              if (n.includes('magnesio') || n.includes('magnesium')) return isEn 
                                ? "Fundamental nervous system regulator. Relaxes nerve impulses to induce deep REM sleep and facilitate nocturnal muscle and hormonal recovery." 
                                : "Regulador fundamental del sistema nervioso. Relaja los impulsos nerviosos para inducir un sueño REM profundo y facilitar la recuperación muscular y hormonal nocturna.";
                              if (n.includes('ashwa')) return isEn 
                                ? "Potent herbal adaptogen. Radically decreases cortisol (stress hormone) levels, creating a more anabolic environment favorable for body recomposition." 
                                : "Potente adaptógeno herbal. Disminuye radicalmente los niveles de cortisol (hormona del estrés), creando un entorno hormonal más anabólico y favorable para la recomposición corporal.";
                              if (n.includes('cafeína') || n.includes('cafe') || n.includes('caffeine')) return isEn 
                                ? "Central nervous system stimulant. Blocks adenosine receptors to reduce perceived fatigue, increase focus and mobilize fatty acids as an energy source." 
                                : "Estimulante del sistema nervioso central. Bloquea los receptores de adenosina para reducir la fatiga percibida, aumentar la concentración y movilizar ácidos grasos como fuente de energía.";
                              if (n.includes('d3') || n.includes('vitamina d') || n.includes('vitamin d')) return isEn 
                                ? "Acts more like a hormone than a vitamin. It is critical to optimize natural testosterone production, bone density, and proper immune system function." 
                                : "Actúa más como una hormona que como vitamina. Es crítica para optimizar la producción natural de testosterona, la densidad ósea y el buen funcionamiento del sistema inmune.";
                              if (n.includes('melatonina') || n.includes('melatonin')) return isEn 
                                ? "Natural sleep signaler. Helps regulate the circadian rhythm to ensure deep and effective rest, which is when muscle growth actually occurs." 
                                : "Señalizador natural del sueño. Ayuda a regular el ritmo circadiano para asegurar un descanso profundo y eficaz, que es cuando realmente ocurre el crecimiento muscular.";
                              return "Suplemento sinérgico seleccionado específicamente para tu objetivo actual.";
                            };

                            const explanationText = s.explicacion || getFallbackExplanation(s.nombre || "");

                            return (
                              <div key={idx} className={`relative transition-all duration-500 py-3 border-b border-white/5 last:border-0 ${isCompleted ? 'opacity-50' : 'opacity-100'}`}>
                                
                                {/* SUPPLEMENT HEADER ROW */}
                                <button
                                  onClick={() => setExpandedSupplement(isExpanded ? null : idx)}
                                  className="w-full flex items-center justify-between text-left relative z-10 group gap-3"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {/* CHEVRON LEFT */}
                                    <div className={`size-7 rounded-full border border-white/10 flex items-center justify-center transition-all duration-300 flex-shrink-0 ${isExpanded ? 'bg-white text-black rotate-180' : 'bg-transparent text-white/40 hover:bg-white/5'}`}>
                                      <span className="material-symbols-outlined text-[10px]">expand_more</span>
                                    </div>

                                    <div className="flex flex-col min-w-0">
                                      <h3 className={`font-black uppercase tracking-wide text-sm sm:text-base leading-tight transition-colors ${
                                        isCompleted ? 'text-white/30 line-through decoration-white/20' : isExpanded ? 'text-primary' : 'text-white group-hover:text-primary'
                                      }`}>
                                        {String(s.nombre || '')}
                                      </h3>
                                      <p className={`text-[11px] mt-0.5 transition-all duration-300 ${isCompleted ? 'text-white/20' : 'text-white/60'}`}>
                                        {String(s.cantidad || s.dosis || s.amount || s.dose || '')} • {String(s.momento || s.timing || '')}
                                      </p>
                                    </div>
                                  </div>

                                  {/* CHECKBOX RIGHT */}
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMealCompletion(supKey);
                                    }}
                                    className={`size-5 sm:size-6 rounded-full border flex items-center justify-center transition-all duration-300 cursor-pointer flex-shrink-0 ${
                                      isCompleted
                                        ? 'bg-[#D4AF37] border-[#D4AF37] text-black shadow-[0_0_10px_rgba(212,175,55,0.4)]'
                                        : 'bg-white/5 border-white/10 text-white/40 hover:border-white/40'
                                    }`}
                                  >
                                    {isCompleted ? (
                                      <span className="material-symbols-outlined text-[12px] sm:text-[14px] font-black">check</span>
                                    ) : (
                                      <span className="size-1.5 sm:size-2 rounded-full bg-white/20 hover:bg-white/40"></span>
                                    )}
                                  </div>
                                </button>

                                {/* SUPPLEMENT EXPANDED CONTENT */}
                                <AnimatePresence>
                                  {isExpanded && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3, ease: "circOut" }}
                                      className="overflow-hidden mt-3 pl-10"
                                    >
                                      <div className="pb-2">
                                        {/* Clean Inline Explanation without Box card */}
                                        <div className="border-l border-primary/30 pl-3 py-0.5">
                                          <p className="text-[11px] text-white/50 leading-relaxed font-medium">
                                            {explanationText}
                                          </p>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        )}
      </div>
      <PendingPhotosModal isOpen={isPhotosModalOpen} onClose={() => setIsPhotosModalOpen(false)} />
    </div>
  );
};

export default NutritionDashboardScreen;
